import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { PageHeader } from '../../layouts/PageHeader'
import { ProjectDetailPeriodControls } from '../ProjectAnalysis/project-detail-period-controls'
import { EmployeeDetailHeaderBar } from './employee-detail-header-bar'
import type { EmployeeDetailPageModel } from '../../../hooks/use-employee-detail-page'
import { appPath } from '../../../utils/appPath'

const backBtnClass =
  'flex items-center gap-1.5 text-[12px] font-medium text-slate-600 border border-slate-200 rounded-lg px-2.5 py-1 hover:bg-white hover:text-slate-900 transition-colors flex-none shrink-0 bg-white/80'

export function EmployeeDetailLoading({ userId }: { userId: number }) {
  const label = `Loading employee ${userId}…`

  return (
    <>
      <PageHeader>
        <div className="flex items-center gap-3 min-w-0">
          <Link to={appPath('/employee-analysis')} className={backBtnClass}>
            <ArrowLeft size={13} /> Back
          </Link>
          <h1 className="text-[15px] font-semibold text-[#0D0D0D] truncate min-w-0">
            {label}
          </h1>
        </div>
      </PageHeader>
      <div className="p-6 flex items-center justify-center py-24">
        <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
        <span className="ml-3 text-slate-500 text-sm">Loading timesheet data…</span>
      </div>
    </>
  )
}

export function EmployeeDetailFetchError({
  message,
}: {
  message: string
}) {
  return (
    <>
      <PageHeader>
        <div className="flex items-center gap-3 min-w-0">
          <Link to={appPath('/employee-analysis')} className={backBtnClass}>
            <ArrowLeft size={13} /> Back
          </Link>
          <h1 className="text-[15px] font-semibold text-[#0D0D0D] truncate min-w-0">
            Employee detail
          </h1>
        </div>
      </PageHeader>
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center max-w-lg">
          <p className="text-red-700 text-sm font-medium">
            Failed to load employee data
          </p>
          <p className="text-red-500 text-xs mt-1">{message}</p>
          <Link
            to={appPath('/employee-analysis')}
            className="mt-4 inline-block text-[12px] font-medium text-blue-600 hover:text-blue-800"
          >
            ← Back to list
          </Link>
        </div>
      </div>
    </>
  )
}

function periodSlotFromModel(m: EmployeeDetailPageModel) {
  return (
    <ProjectDetailPeriodControls
      detailMode={m.detailMode}
      onDetailModeChange={m.setDetailMode}
      detailFY={m.detailFY}
      onDetailFYChange={m.setDetailFY}
      detailQ={m.detailQ}
      onDetailQChange={m.setDetailQ}
      detailYear={m.detailYear}
      onDetailYearChange={m.setDetailYear}
      detailMonth={m.detailMonth}
      onDetailMonthChange={m.setDetailMonth}
      detailFYOptions={m.profileFYOptions}
      detailYearOptions={m.profileYearOptions}
      className="w-full lg:w-auto justify-end"
    />
  )
}

/** No JSON / empty array for this user. */
export function EmployeeDetailNoData({ m }: { m: EmployeeDetailPageModel }) {
  const periodSlot = periodSlotFromModel(m)
  return (
    <>
      <PageHeader>
        <EmployeeDetailHeaderBar
          title={m.headerTitle}
          statusLabel={m.headerStatusActive ? 'Active' : 'Inactive'}
          email={m.headerEmail}
          username={m.headerUsername}
          userId={m.userId}
          statusPill="No timesheet data"
          periodSlot={periodSlot}
        />
      </PageHeader>
      <div className="p-6 space-y-3">
        <p className="text-sm text-slate-600">
          There is no gold-fact data for this employee. Regenerate user JSON if
          needed.
        </p>
      </div>
    </>
  )
}

/** Data exists but selected period has no rows. */
export function EmployeeDetailEmptyPeriod({ m }: { m: EmployeeDetailPageModel }) {
  const periodSlot = periodSlotFromModel(m)
  return (
    <>
      <PageHeader>
        <EmployeeDetailHeaderBar
          title={m.headerTitle}
          statusLabel={m.headerStatusActive ? 'Active' : 'Inactive'}
          email={m.headerEmail}
          username={m.headerUsername}
          userId={m.userId}
          statusPill="No rows for this period"
          periodSlot={periodSlot}
        />
      </PageHeader>
      <div className="p-6 space-y-3">
        <p className="text-sm text-slate-600">
          Adjust FY or calendar filters in the header, or go back to the list.
        </p>
      </div>
    </>
  )
}
